import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Briefcase,
  Globe2,
  MessageCircle,
  Package,
  Repeat,
  ShieldCheck,
  Store,
  Wallet,
} from "lucide-react";
import DealWalkthrough from "./landing/DealWalkthrough";

type Theme = "dark" | "light";
const AUDIENCES = [
  {
    title: "Buyers",
    body: "Find products, ask questions, and agree on the details before paying.",
    cta: "Find what you need",
    href: "/sign-up?role=buyer",
    icon: ShieldCheck,
  },
  {
    title: "Sellers",
    body: "Share your products and turn conversations with buyers into orders.",
    cta: "Start selling",
    href: "/sign-up?role=seller",
    icon: Store,
  },
  {
    title: "Freelancers",
    body: "Show your work, discuss the brief, and agree on a paid service deal.",
    cta: "Get clients",
    href: "/sign-up?role=freelancer",
    icon: Briefcase,
  },
  {
    title: "Resellers",
    body: "Connect with suppliers, negotiate quantities, and build repeat business.",
    cta: "Start reselling",
    href: "/sign-up?role=reseller",
    icon: Repeat,
  },
];
const USE_CASES = [
  {
    title: "Physical goods",
    body: "Products, handmade items, and local inventory.",
    icon: Package,
  },
  {
    title: "Digital products",
    body: "Files, templates, creative assets, and downloads.",
    icon: Globe2,
  },
  {
    title: "Services",
    body: "Design, consulting, repairs, photography, and more.",
    icon: Briefcase,
  },
  {
    title: "Resale",
    body: "Supplier orders and repeat inventory purchases.",
    icon: Repeat,
  },
];
const TRUST_POINTS = [
  {
    title: "Protected payment",
    body: "Funds are held during the deal. Release follows buyer confirmation or the delivery review deadline, unless a dispute is open.",
    icon: ShieldCheck,
  },
  {
    title: "Direct chat",
    body: "Agree on scope, price, and delivery before paying. Keep deal updates and evidence in the conversation.",
    icon: MessageCircle,
  },
  {
    title: "Fees before payment",
    body: "Buyers see their total and sellers see their estimated payout before committing. Additional order payments have a fee breakdown too.",
    icon: BadgeCheck,
  },
  {
    title: "Cancellation and disputes",
    body: "Request cancellation when eligible, or raise a dispute within the delivery review window. Refunds are finalized after payment-provider confirmation.",
    icon: Wallet,
  },
];
const FLOW_STEPS = [
  {
    title: "Discover",
    body: "Explore posts and find a product, service, or person you want to work with.",
  },
  {
    title: "Discuss and agree",
    body: "Use chat to agree on requirements, the price, and delivery expectations.",
  },
  {
    title: "Pay with protection",
    body: "Review the amount and fees. Your protected payment is held while the deal progresses.",
  },
  {
    title: "Track delivery",
    body: "Follow shipping or work progress in the deal tracker attached to your chat.",
  },
  {
    title: "Review the result",
    body: "After delivery, confirm completion or raise a dispute within the displayed review window.",
  },
  {
    title: "Complete and reconnect",
    body: "Eligible funds are released and payout is confirmed by the provider. Rate your experience and start a fresh deal when you are ready.",
  },
];

export default function Landing() {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return typeof window !== "undefined" &&
        localStorage.getItem("theme") === "light"
        ? "light"
        : "dark";
    } catch {
      return "dark";
    }
  });
  const dark = theme === "dark";
  const muted = dark ? "text-gray-400" : "text-gray-600";
  const card = dark
    ? "bg-gray-900 border-gray-800"
    : "bg-white border-gray-200";
  const soft = dark
    ? "bg-gray-900/60 border-gray-800"
    : "bg-gray-50 border-gray-200";
  const outline = dark
    ? "border-gray-700 hover:bg-gray-800"
    : "border-gray-300 hover:bg-gray-50";
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.classList.toggle("light", theme === "light");
    try {
      localStorage.setItem("theme", theme);
    } catch {
      /* Theme still works when storage is unavailable. */
    }
  }, [theme]);

  return (
    <div
      className={`min-h-screen transition-colors ${dark ? "bg-[#09090b] text-white" : "bg-white text-gray-900"}`}>
      <header
        className={`sticky top-0 z-40 backdrop-blur-md border-b ${dark ? "bg-[#09090b]/90 border-gray-800" : "bg-white/90 border-gray-200"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-3">
          <Link
            to="/"
            className="text-2xl sm:text-3xl font-light"
            style={{ fontFamily: "Georgia, serif" }}>
            busiconn
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              aria-label="Toggle theme"
              onClick={() => setTheme(dark ? "light" : "dark")}
              className={`p-2 rounded-lg ${outline}`}>
              {dark ? "🌙" : "☀️"}
            </button>
            <Button
              asChild
              variant="ghost"
              className={`hidden sm:inline-flex ${muted}`}>
              <Link to="/home">Explore marketplace</Link>
            </Button>
            <Button
              asChild
              className="bg-blue-600 hover:bg-blue-700 text-white">
              <Link to="/sign-up">Join now</Link>
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        <section className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-12 items-start">
          <div className="max-w-3xl lg:pt-10">
            <p className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-blue-500/20 bg-blue-500/10 text-blue-600 dark:text-blue-300 text-xs sm:text-sm font-medium">
              <MessageCircle className="h-4 w-4 shrink-0" /> A social platform
              for protected deals
            </p>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
              Discover people.
              <br />
              Discuss the details.
              <br />
              <span className="text-blue-500">Deal with confidence.</span>
            </h1>
            <p
              className={`mt-5 text-base sm:text-lg max-w-2xl leading-relaxed ${muted}`}>
              Find products, freelancers, and business opportunities through
              posts. Chat directly, agree on a price, and track your protected
              deal from payment to completion-all in one conversation.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row flex-wrap gap-3">
              <Button
                asChild
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5">
                <Link to="/sign-up?role=seller">
                  I want to sell
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className={outline}>
                <Link to="/sign-up?role=buyer">I want to buy</Link>
              </Button>
              <Button asChild variant="ghost" className={muted}>
                <a href="#deal-walkthrough">See how it works</a>
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm">
              <span className="inline-flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-blue-500" />
                Direct chat
              </span>
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-500" />
                Protected payment
              </span>
              <span className="inline-flex items-center gap-2">
                <BadgeCheck className="w-4 h-4 text-blue-500" />
                Deal tracking
              </span>
            </div>
          </div>
          <DealWalkthrough theme={theme} />
        </section>
        <section
          className={`mt-12 sm:mt-16 rounded-3xl p-6 sm:p-8 border ${soft}`}
          aria-labelledby="deal-flow-heading">
          <p className="text-sm font-semibold text-blue-500">
            FROM FIRST MESSAGE TO COMPLETION
          </p>
          <h2
            id="deal-flow-heading"
            className="mt-2 text-2xl sm:text-3xl font-semibold">
            Your conversation becomes your deal.
          </h2>
          <p className={`mt-3 max-w-2xl ${muted}`}>
            Keep the agreed details, payment status, and delivery updates
            together, so both people know what happens next.
          </p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {FLOW_STEPS.map((step, index) => (
              <div
                key={step.title}
                className={`p-5 rounded-2xl border ${card}`}>
                <span className="text-blue-500 text-sm font-semibold">
                  0{index + 1}
                </span>
                <h3 className="mt-2 font-semibold">{step.title}</h3>
                <p className={`mt-2 text-sm leading-6 ${muted}`}>{step.body}</p>
              </div>
            ))}
          </div>
        </section>
        <section className="mt-12 sm:mt-16" aria-labelledby="audience-heading">
          <p className="text-sm font-semibold text-blue-500">
            FIND YOUR NEXT OPPORTUNITY
          </p>
          <h2
            id="audience-heading"
            className="mt-2 text-2xl sm:text-3xl font-semibold">
            For what you need. For what you offer.
          </h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {AUDIENCES.map(({ title, body, cta, href, icon: Icon }) => (
              <div
                key={title}
                className={`p-5 rounded-2xl border flex flex-col ${card}`}>
                <Icon className="h-6 w-6 text-blue-500" />
                <h3 className="mt-4 font-semibold text-lg">{title}</h3>
                <p className={`mt-2 mb-5 text-sm leading-6 ${muted}`}>{body}</p>
                <Link
                  to={href}
                  className="mt-auto text-sm text-blue-500 font-semibold inline-flex items-center gap-2">
                  {cta}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>
        <section
          className={`mt-12 sm:mt-16 rounded-3xl p-6 sm:p-8 border ${soft}`}
          aria-labelledby="protection-heading">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6 lg:gap-8 items-start">
            <div>
              <p className="text-sm font-semibold text-blue-500">
                KNOW WHERE YOUR DEAL STANDS
              </p>
              <h2
                id="protection-heading"
                className="mt-2 text-2xl sm:text-3xl font-semibold">
                Clear terms. Visible progress. Protected payment.
              </h2>
              <p className={`mt-4 leading-relaxed ${muted}`}>
                Agree before you pay. Follow the deal in chat. If something goes
                wrong, use the cancellation or dispute options available for
                your deal.
              </p>
              <Link
                to="/faq"
                className="mt-5 inline-flex items-center gap-2 text-blue-500 font-semibold text-sm">
                Read about fees and protection
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {TRUST_POINTS.map(({ title, body, icon: Icon }) => (
                <div key={title} className={`p-4 rounded-2xl border ${card}`}>
                  <Icon className="h-5 w-5 text-blue-500" />
                  <h3 className="mt-3 font-semibold">{title}</h3>
                  <p className={`mt-2 text-sm leading-6 ${muted}`}>{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        <section
          className="mt-12 sm:mt-16"
          aria-labelledby="categories-heading">
          <h2
            id="categories-heading"
            className="text-2xl sm:text-3xl font-semibold">
            Products, services, and the details in between.
          </h2>
          <p className={`mt-3 ${muted}`}>
            A custom order starts with a conversation. Tell the other person
            what you need and agree on the deal together.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {USE_CASES.map(({ title, body, icon: Icon }) => (
              <div key={title} className={`p-5 rounded-2xl border ${card}`}>
                <Icon className="h-5 w-5 text-blue-500" />
                <h3 className="mt-3 font-semibold">{title}</h3>
                <p className={`mt-2 text-sm leading-6 ${muted}`}>{body}</p>
              </div>
            ))}
          </div>
        </section>
        <section
          className={`mt-12 sm:mt-16 p-6 sm:p-8 rounded-3xl border ${soft}`}>
          <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold">
                Your next deal starts with a conversation.
              </h2>
              <p className={`mt-3 ${muted}`}>
                Explore what people offer, share your own work, and connect when
                you find the right fit.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                className="bg-blue-600 hover:bg-blue-700 text-white">
                <Link to="/sign-up">Create your account</Link>
              </Button>
              <Button asChild variant="outline" className={outline}>
                <Link to="/home">Explore marketplace</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <footer
        className={`mt-12 border-t ${dark ? "border-gray-800" : "border-gray-200"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col md:flex-row md:justify-between gap-6">
          <div>
            <p className="font-semibold text-lg">busiconn</p>
            <p className={`text-sm mt-2 ${muted}`}>
              Discover. Connect. Make a protected deal.
            </p>
          </div>
          <nav
            aria-label="Footer"
            className={`flex flex-wrap items-center gap-6 text-sm ${muted}`}>
            <Link to="/faq" className="hover:underline">
              FAQ
            </Link>
            <Link to="/privacy-policy" className="hover:underline">
              Privacy
            </Link>
            <Link to="/termsandconditions" className="hover:underline">
              Terms
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
